import "devextreme/dist/css/dx.common.css";
import "./themes/generated/theme.base.css";
import "./themes/generated/theme.additional.css";
import React, { useEffect, useState } from "react";
import { HashRouter as Router } from "react-router-dom";
import "./dx-styles.scss";
import "./theme/dark-mode.scss";
import LoadPanel from "devextreme-react/load-panel";
import { NavigationProvider } from "./contexts/navigation";
import { UnsavedWorkProvider } from "./contexts/unsavedWork";
import { AuthProvider, useAuth } from "./contexts/auth";
import { useScreenSizeClass } from "./utils/media-query";
import Content from "./Content";
import OrganizationGate from "./components/organization-gate/organization-gate";
import UnauthenticatedContent from "./UnauthenticatedContent";
import { Context } from "./utils/config";
import { Toaster } from "react-hot-toast";
import "react-phone-input-2/lib/style.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeModeProvider } from "./contexts/theme-mode";
import { installSafariHashHistorySync } from "./utils/safariHashHistory";

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadPanel visible={true} />;
  }

  if (user) {
    return (
      <OrganizationGate>
        <Content />
      </OrganizationGate>
    );
  }

  return <UnauthenticatedContent />;
}

export default function Root() {
  const screenSizeClass = useScreenSizeClass();
  // Hash history on Safari can miss the route after Back. Re-read the hash
  // once it has settled so the page outlet matches the URL.
  useEffect(() => installSafariHashHistorySync(), []);
  const [token, setToken] = useState(null);
  const [dataset, setDataset] = useState({});
  // JD-293: Initialize selected location from sessionStorage if available.
  // After login, AuthProvider selects the user's only assigned location, or
  // All Locations when they have more than one. The header selector updates
  // both sessionStorage and this state on change.
  const [location, setLocation] = useState(() => {
    try {
      const storedId = sessionStorage.getItem("selectedLocationId");
      const storedName = sessionStorage.getItem("selectedLocationName");
      const parsed = parseInt(storedId, 10);
      if (storedId && !isNaN(parsed)) {
        return { location_id: parsed, location_name: storedName || null };
      }
    } catch (e) {
      /* sessionStorage unavailable */
    }
    return { location_id: -1, location_name: "All Locations" };
  });

  return (
    <ErrorBoundary>
      <ThemeModeProvider>
        <Router>
          <Context.Provider
            value={{
              token,
              setToken,
              dataset,
              setDataset,
              location,
              setLocation,
            }}
          >
            <AuthProvider>
              <NavigationProvider>
                <UnsavedWorkProvider>
                  <div className={`app ${screenSizeClass}`}>
                    <App />
                    <Toaster />
                  </div>
                </UnsavedWorkProvider>
              </NavigationProvider>
            </AuthProvider>
          </Context.Provider>
        </Router>
      </ThemeModeProvider>
    </ErrorBoundary>
  );
}
